// src/shared_new/components/PerlCostTable.jsx
// Reusable Cost Breakdown Table Component with Perl Theme

import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { theme } from "../../design/theme";

const { perlColors, perlTypography } = theme.custom;

const headerCell = {
  ...perlTypography.headline,
  bgcolor: perlColors.headerBg || "#303436",
  color: perlColors.textSecondary,
  borderBottom: `1px solid ${perlColors.border}`,
  borderTop: `1px solid ${perlColors.border}`,
  py: 0.5,
  px: 1.5,
  fontSize: "16px",
  lineHeight: "22px",
};

const bodyCell = {
  ...perlTypography.label,
  color: perlColors.textPrimary,
  borderBottom: `1px solid ${perlColors.border}`,
  py: 0.5,
  px: 1.5,
  fontSize: "16px",
  lineHeight: "22px",
};

const f4 = (n) => Number(n ?? 0).toFixed(4);
const f1p = (n) => `${Number(n ?? 0).toFixed(1)}%`;
const num = (n) => Number(n ?? 0);

/**
 * PerlCostTable - Display cost breakdown with categories and percentages
 *
 * @param {Object} props
 * @param {Object} props.breakdown - Cost breakdown (unused when rows provided)
 * @param {Array} props.rows - Array of cost rows: [{ key, label, value }]
 * @param {number} props.total - Optional total override
 * @param {string} props.brandColor - Brand color for percentage bars (default: #ff6034)
 * @param {number} props.ohpRate - Optional OHP rate percentage to use for OHP row instead of calculated percentage
 */
export default function PerlCostTable({
  breakdown,
  rows = [],
  total: totalOverride,
  brandColor = "#ff6034",
  ohpRate = null,
}) {
  // Use rows provided by the caller
  const normalizedRows = (rows || []).map((r, idx) => ({
    key: r.key || r.label || `row-${idx}`,
    label: r.label || r.name || r.key || `Row ${idx + 1}`,
    value: num(r.value),
  }));

  const b = breakdown || {};
  const currency = b.currency || "USD/unit";

  const totalFromProp = Number(totalOverride);
  const total = Number.isFinite(totalFromProp)
    ? totalFromProp
    : normalizedRows.reduce((sum, r) => sum + num(r.value), 0);

  const pct = (v) => (total > 0 ? (num(v) / total) * 100 : 0);

  // Use provided rows or default to standard breakdown
  // const computedRows = rows || [
  //   { label: "Material", key: "material", value: num(b.material) },
  //   { label: "Process", key: "process", value: num(b.process) },
  //   { label: "OHP", key: "ohp", value: num(b.ohp) },
  // ];

  return (
    <Box sx={{ display: "grid", gap: 2, height: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          border: `1px solid ${perlColors.border}`,
          bgcolor: perlColors.surface,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCell}>Cost Category</TableCell>
              <TableCell sx={headerCell} align="right">
                Amount (USD/Unit)
              </TableCell>
              <TableCell
                sx={{
                  ...headerCell,
                  textAlign: "right",
                  pl: 2,
                  minWidth: 160,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      minWidth: 50,
                      textAlign: "left",
                      color: `${perlColors.textSecondary} !important`,
                      fontSize: "16px",
                      fontFamily: perlTypography.family,
                      fontWeight: 500,
                      letterSpacing: "0.3px",
                    }}
                  >
                    %
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                </Box>
              </TableCell>
              <TableCell sx={{ ...headerCell, minWidth: 90 }}>Currency</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {normalizedRows.map((r) => {
              // Use OHP rate from playbook if available and this is the OHP row
              const share = (r.key === "ohp" && ohpRate !== null && ohpRate !== undefined) 
                ? Number(ohpRate) 
                : pct(r.value);
              return (
                <TableRow key={r.key} sx={{ "&:hover": { bgcolor: "rgba(241, 242, 243, 0.04)" } }}>
                  <TableCell sx={bodyCell}>{r.label}</TableCell>
                  <TableCell sx={{ ...bodyCell, textAlign: "right", pr: 2, pl: 2 }}>
                    ${f4(r.value)}
                  </TableCell>

                  {/* % + inline bar */}
                  <TableCell sx={{ ...bodyCell, textAlign: "right", minWidth: 160, pl: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Typography
                        sx={{
                          minWidth: 50,
                          fontSize: "16px",
                          lineHeight: "22px",
                          fontFamily: perlTypography.family,
                          letterSpacing: "0.3px",
                          textAlign: "left",
                        }}
                      >
                        {f1p(share)}
                      </Typography>
                      <Box
                        sx={{
                          flex: 1,
                          height: 6,
                          bgcolor: "rgba(241, 242, 243, 0.08)",
                          borderRadius: 999,
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${Math.min(100, Math.max(0, share))}%`,
                            height: "100%",
                            bgcolor: brandColor,
                            transition: "width 0.4s ease",
                          }}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...bodyCell, minWidth: 90 }}>{currency}</TableCell>
                </TableRow>
              );
            })}

            {/* Total row */}
            <TableRow
              sx={{
                "&:hover": { bgcolor: "rgba(241, 242, 243, 0.04)" },
                borderTop: `2px solid ${perlColors.border}`,
              }}
            >
              <TableCell
                sx={{
                  fontFamily: perlTypography.family,
                  fontWeight: 500,
                  fontSize: "16px",
                  lineHeight: "22px",
                  letterSpacing: "0.3px",
                  color: perlColors.textPrimary,
                  borderBottom: "none",
                  py: 0.5,
                  px: 1.5,
                }}
              >
                Total
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: perlTypography.family,
                  fontWeight: 500,
                  fontSize: "16px",
                  lineHeight: "22px",
                  letterSpacing: "0.3px",
                  color: perlColors.textPrimary,
                  textAlign: "right",
                  borderBottom: "none",
                  py: 0.5,
                  pr: 2,
                  pl: 2,
                }}
              >
                ${f4(total)}
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: perlTypography.family,
                  fontWeight: 500,
                  fontSize: "16px",
                  lineHeight: "22px",
                  letterSpacing: "0.3px",
                  color: perlColors.textPrimary,
                  textAlign: "right",
                  minWidth: 160,
                  borderBottom: "none",
                  pl: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    justifyContent: "flex-end",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 500,
                      minWidth: 50,
                      fontSize: "16px",
                      lineHeight: "22px",
                      fontFamily: perlTypography.family,
                      letterSpacing: "0.3px",
                      textAlign: "left",
                      color: perlColors.textPrimary,
                    }}
                  >
                    {f1p(100)}
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      height: 6,
                      bgcolor: "rgba(241, 242, 243, 0.08)",
                      borderRadius: 999,
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        bgcolor: brandColor,
                        transition: "width 0.4s ease",
                      }}
                    />
                  </Box>
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  ...bodyCell,
                  fontWeight: 500,
                  minWidth: 90,
                  borderBottom: "none",
                }}
              >
                {currency}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
